import { createContext, useContext } from 'react';
import { useFacebookPixel, useGoogleAnalytics } from '../hooks';
import Script from 'next/script';
// import useSegment from '../hooks/use-segment';
// import useMixpanel from '../hooks/use-mixpanel';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import usePostHogProvider from '../hooks/use-posthog-provider';
import { useOpenObserveIdentify } from '../hooks/use-openobserve-identify';

export type EventName =
  | 'worker_successfully_applied'
  | 'start_auth_flow'
  | 'upload_cv'
  | 'add_details_cv'
  | 'check_data_cv'
  | 'initiate_questionnaire'
  | 'questionnaire_progress'
  | 'view_job'
  | 'show_more_locations'
  | 'share_job'
  | 'apply_job'
  | 'spontaneous_apply'
  | 'initiate_save_search'
  | 'save_search_job_details'
  | 'complete_save_search'
  | 'initiate_signin'
  | 'initiate_create_account'
  | 'create_account'
  | 'send_sms'
  | 'complete_signin'
  | 'initiate_apply_job'
  | 'initiate_upload_cv'
  | 'apply_intent_resumed'
  // The already-registered visitor who uploads a CV while logged out. `..._wall_shown` is the
  // denominator we could only get from the `cv_upload_intents` table until now; `..._login_clicked`
  // says whether the way out is taken, and `placement` says which of the two ways out.
  // Deliberately not prefixed `worker_`: at this point in the flow nobody is signed in, and the
  // person may never become a worker at all.
  | 'existing_account_wall_shown'
  | 'existing_account_login_clicked'
  | 'worker_request_otp_for_login'
  | 'worker_request_otp_for_phone_number_change'
  | 'worker_opens_references_page'
  | 'worker_visible_reference'
  | 'worker_enables_references'
  | 'worker_viewed_offline_job_position'
  | 'worker_subscribed_to_similar_jobs_alert'
  | 'worker_clicked_similar_job_from_offline'
  | 'worker_clicked_explore_from_offline'
  | 'worker_started_spontaneous_app_from_offline'
  | 'worker_referral_program_viewed'
  | 'worker_referral_program_enrolled'
  | 'worker_referral_link_copied'
  | 'worker_referral_link_shared'
  // The in-app interview with Serena (`/candidature/[id]/intervista`). Nothing in this flow was
  // measured until now: the chat shipped with its own error reporting and no events at all, so a
  // worker who opened the interview and stopped answering was indistinguishable from one who
  // never opened it. Named `<actor>_<feature>_<past participle>`, the convention the recent
  // employer events settled on.
  | 'worker_interview_opened'
  | 'worker_interview_message_sent'
  | 'worker_interview_completed'
  // The browser-notifications soft ask in the interview header. v1 asks for the permission and
  // nothing sends a notification yet, so these two events are the only way to know whether the
  // button is worth building the push behind: how many are asked, and what they answer.
  | 'worker_interview_notifications_prompted'
  | 'worker_interview_notifications_answered'
  // The screen a worker sees after applying, which differs by assessment channel: the point of
  // the event is the channel, not the screen.
  | 'worker_apply_confirmation_shown'
  | 'worker_profile_completed'
  // One name for every call to action, with the button in `cta` and the screen in `surface`.
  // Deliberately not one event name per button: the list of buttons changes with every release
  // and a name per button turns that into a migration of dashboards.
  | 'worker_cta_clicked';

export type EmployerEventName =
  | 'employer_view_jp_details'
  | 'employer_clicks_ad_performance'
  | 'employer_manage_candidate'
  | 'employer_discard_candidate'
  | 'employer_schedule_interview'
  | 'employer_add_note'
  | 'employer_download_CV'
  | 'employer_view_locali_details'
  | 'employer_click_qr_code'
  | 'employer_share_social_media'
  | 'employer_view_payment'
  | 'employer_buy_slot'
  | 'employer_activate_slot'
  | 'employer_cancel_slot'
  | 'employer_add_payment'
  | 'employer_click_support'
  | 'employer_open_new_position'
  | 'employer_click_homepage'
  | 'employer_click_job_positions'
  | 'employer_click_venues'
  | 'employer_click_spontaneous_applications'
  | 'ex_employer_submit_reference_step_one'
  | 'ex_employer_submit_reference_step_two'
  | 'ex_employer_submit_reference_step_three'
  | 'ex_employer_submit_reference_success'
  | 'ex_employer_click_what_is_restworld'
  | 'employer_view_candidate_references'
  | 'visualised_spontaneous_candidate' // to track the number of times an employer open (and therefore reads) the modal of a new spont. candidate
  | 'employer_publish_jp'
  | 'employer_jp_goes_offline'
  | 'referral_venue_clicked'
  | 'referral_request_invitation_clicked'
  // referral funnel: entry points (nav CTA, dashboard card) → page → share modal → channel.
  // Cross with server-side referral_credits to find where the funnel leaks.
  | 'referral_share_modal_opened'
  | 'referral_share_channel_clicked'
  | 'referral_dashboard_card_viewed'
  | 'referral_dashboard_card_clicked'
  | 'referral_nav_cta_clicked'
  | 'referral_page_viewed'
  | 'employer_onboarding_step_completed'
  | 'employer_onboarding_intro_cta_clicked'
  | 'employer_onboarding_google_search_no_result'
  | 'employer_onboarding_restaurant_created_manually'
  // Manual venue entry: the employer typed an address without picking a
  // suggestion (blocked), or picked one that we failed to geocode.
  | 'employer_onboarding_manual_address_blocked'
  | 'employer_onboarding_manual_address_geocoding_failed'
  // Venue form: Continue clicked while some fields are invalid. Carries the
  // invalid field names only (never their values) and whether it is manual entry.
  | 'employer_onboarding_restaurant_form_invalid'
  // Org-create (POST /organizations) failure: status + backend reason + which
  // fields/flow failed. Pairs with the backend "OrgCreateFailure" structured log
  // so a getting-started drop-off can be diagnosed end to end.
  | 'employer_onboarding_org_create_failed'
  // Getting-started "company and payment" step funnel (measurement only, see
  // apps/employer-app/components/job-position-onboarding/hooks/use-company-and-payment-analytics.ts).
  // Every event carries step, sub_view, organization_id, jp_id, payment_readiness_status,
  // host and vercel_env: stage and production share one PostHog project, so host and
  // vercel_env are what keep them apart. `view_entered` / `view_exited` fire on every
  // sub-view change (exited carries `to` and `duration_ms`, and `to: 'left_step'` when the
  // step unmounts). There is no unload event: abandonment is the last `view_entered` with
  // nothing after it in the same PostHog session ($session_id) for that organization_id and
  // jp_id. The other events record each attempt and its outcome: the readiness check on
  // load (including its failure and the unpaid-invoices modal), company pick and submit,
  // payment method save (card, Apple/Google Pay, PayPal including its redirect return), and
  // publish. Errors carry enumerations and HTTP statuses only, never error messages.
  | 'employer_onboarding_payment_view_entered'
  | 'employer_onboarding_payment_view_exited'
  | 'employer_onboarding_payment_readiness_checked'
  | 'employer_onboarding_company_selected'
  | 'employer_onboarding_company_submitted'
  | 'employer_onboarding_payment_method_submitted'
  | 'employer_onboarding_publish_submitted'
  // discard → match-review funnel: tracks how many recommended-candidate discards
  // see (and act on) the educational match-review modal. Filter session
  // recordings by `employer_match_review_viewed` to watch what the employer
  // actually does in front of the modal.
  | 'employer_discard_submitted'
  | 'employer_match_review_consent_accepted'
  | 'employer_match_review_consent_dismissed'
  | 'employer_match_review_viewed'
  | 'employer_match_review_edit_ad_clicked'
  | 'employer_shares_job_position'
  // Job Doctor (Dr. Job) satisfied-state feedback on candidate quality: 👍/👎 on the
  // applications received. `rating` = 'up' | 'down'; 👎 also opens a PostHog survey for
  // the free-text detail (that survey is displayed via posthog directly, not this event).
  | 'job_doctor_candidate_quality'
  // Job Doctor usage funnel. `viewed` fires once per opening of the modal, from the modal
  // itself, as soon as the analysis query settles — so `source` separates the three ways in
  // (the "Dr. Job" menu item, the day 1/2/5 auto-open, the `?analysis=1` deep link in the
  // recap email) and `state` says what the employer actually got ('ok' | 'texts_pending' |
  // 'empty'). Filter session recordings by it to watch a real reading of the analysis.
  // The lever pair is the conversion: `accepted` fires when the server confirms the queued
  // change, `undone` when it is cancelled before the debounce — count accepted minus undone,
  // or the number is inflated by mis-clicks. Both carry `lever`, so which advice actually
  // lands is answerable. Analysis-level properties (`doctor_day`, the two macro-area bands,
  // `satisfied`) ride on all three; survey answers stay in our own DB and are joined there.
  | 'employer_job_doctor_viewed'
  | 'employer_job_doctor_lever_accepted'
  | 'employer_job_doctor_lever_undone'
  // Preselection-candidates A/B (flag `employer-preselection-candidate-cards`):
  // swipeable card stack (card_stack) vs current list (control). PostHog
  // auto-tags both events with `$feature/<flag>`, so the variant split needs no
  // manual property. `opened` = engagement metric, `moved_to_kanban` = conversion.
  | 'employer_preselection_candidate_opened'
  | 'employer_preselection_candidate_moved_to_kanban'
  // Transparency banner research A/B test (flag `ab-transparency-banner`, org-level
  // 50/50). `shown` fires on every render of the banner (repeated exposures are
  // counted by design); `info_clicked` tracks the "learn more" CTA opening the AI
  // preselection modal. Analysis happens outside PostHog on an anonymized extract.
  | 'employer_transparency_banner_shown'
  | 'employer_transparency_banner_info_clicked'
  // Preselection-params survey (research study, PostHog API-mode survey rendered by us).
  // `survey shown` / `survey sent` are PostHog's own reserved event names — the space is
  // required, it is what makes the responses show up in the Surveys UI and CSV export.
  // `geo_survey_step_completed` is deliberately OURS: partial answers must stay out of the
  // researchers' export, which has to be one row per organization and role.
  | 'survey shown'
  | 'survey sent'
  | 'geo_survey_step_completed';

type AnalyticsContextType = {
  isActive: boolean;
  trackEvent: (eventName: EventName | EmployerEventName, eventProperties: Record<string, unknown>) => void;
  fbTrackWorkerApplied: () => void;
  fbTrackWorkerStartsApplication: () => void;
  fbTrackWorkerAppliesSpontaneously: () => void;
  trackPHEvent: (eventName: string, eventProperties: Record<string, unknown>) => void;
};
export const AnalyticsContext = createContext<AnalyticsContextType>({
  isActive: false,
  trackEvent: (event, prop) => null,
  trackPHEvent: (event, prop) => null,
  fbTrackWorkerApplied: () => null,
  fbTrackWorkerStartsApplication: () => null,
  fbTrackWorkerAppliesSpontaneously: () => null,
});

const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';
// const clarityProjectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID ?? '';

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  // const { isActive, trackEvent } = useMixpanel();
  const { trackEvent: trackPHEvent } = usePostHogProvider();
  useOpenObserveIdentify();
  // const { isActive, trackEvent } = useSegment();
  useGoogleAnalytics();
  const {
    trackWorkerApplied: fbTrackWorkerApplied,
    trackWorkerStartsApplication: fbTrackWorkerStartsApplication,
    trackWorkerAppliesSpontaneously: fbTrackWorkerAppliesSpontaneously,
  } = useFacebookPixel();

  return (
    <>
      {/* Clarity global script */}
      {/* {isProd && (
        <Script strategy="lazyOnload">
          {`
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${clarityProjectId}");
        `}
        </Script>
      )} */}

      {/* Google Tag global script */}
      {isProd && (
        <>
          <Script strategy="lazyOnload" async src="https://www.googletagmanager.com/gtag/js?id=AW-622219312" />
          <Script strategy="lazyOnload">
            {`window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config','AW-622219312');`}
          </Script>
        </>
      )}

      <PostHogProvider client={posthog}>
        <AnalyticsContext.Provider
          value={{
            isActive: false,
            trackEvent: trackPHEvent,
            fbTrackWorkerApplied,
            fbTrackWorkerStartsApplication,
            fbTrackWorkerAppliesSpontaneously,
            trackPHEvent,
          }}
        >
          {children}
        </AnalyticsContext.Provider>
      </PostHogProvider>
    </>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within a AnalyticsProvider');
  }
  return context;
};

export const trackEmployerFreekiumSignupGtag = () => {
  if (window && (window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      send_to: 'AW-622219312/lP8KCLPA68YaELCg2agC',
    });
  }
};

export const trackWorkerAppliesForJP = () => {
  if (window && (window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      send_to: 'AW-622219312/9ejYCOqu150bELCg2agC',
    });
  }
};

export const trackWorkerAppliesForShowcase = () => {
  if (window && (window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      send_to: 'AW-622219312/v-c9CO2u150bELCg2agC',
    });
  }
};

export const trackWorkerSavesTheResearch = () => {
  if (window && (window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      send_to: 'AW-622219312/ljnfCPCu150bELCg2agC',
    });
  }
};

export const trackEmployerSuccessfullyBuys = () => {
  if (window && (window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      send_to: 'AW-622219312/n0DOCP-Y2JEcELCg2agC',
    });
  }
};

export const trackCreateAccount = () => {
  if (window && (window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      send_to: 'AW-622219312/CnPoCLG29ZMcELCg2agC',
      value: 1.0,
      currency: 'EUR',
    });
  }
};
